import { fetchTrack, fetchAudioToken, fetchPlaylist } from '~/utils/audioplayer';
import { defineStore } from '#imports';

export const useAudioPlayerStore = defineStore('audioplayer', {
  state: () => {
    return {
      audioPlayerToken: undefined,
      isPlaying: false,
      loadedProductDetails: undefined,
      loadedWaveSurfer: null,
      showFooterBar: false,
      footerBarVisible: false,
      shouldPlay: false,
      audioErrorMessage: '',
      userClickedPlay: false,
      triggeredByScroll: false,
    }
  },
  getters: {
    getAudioPlayerToken(state) {
      return state.audioPlayerToken
    },
    getLoadedWaveSurfer(state) {
      return state.loadedWaveSurfer
    },
  },
  actions: {
    setAudioPlayerToken(token) {
      this.audioPlayerToken = token
    },
    setProductDetails(productDetails) {
      this.loadedProductDetails = productDetails
    },
    setLoadedWaveSurfer(waveSurfer) {
      if (this.loadedWaveSurfer) {
        this.loadedWaveSurfer.destroy()
      }
      this.loadedWaveSurfer = waveSurfer
    },
    async fetchTrack(trackId, retryCount = 0) {
      if (!this.audioPlayerToken) {
        await fetchAudioToken()
      }
      
      if (!this.audioPlayerToken) {
        if (retryCount < 2) {
          return this.fetchTrack(trackId, retryCount + 1);
        }
        console.error('audio error. Too many retries.');
        return null;
      }

      // First try to fetch as track
      const trackResult = await fetchTrack(trackId);
      if (trackResult) {
        return trackResult;
      }

      // If not a track, try as playlist
      const playlistResult = await fetchPlaylist(trackId);
      if (playlistResult) {
        return playlistResult;
      }

      if (retryCount < 2) {
        return this.fetchTrack(trackId, retryCount + 1);
      }
      
      console.error('audio error. Too many retries.');
      return null;
    },
    async fetchTrackWithHandling(trackId, productDetails, retryCount = 0) {
      const url = await this.fetchTrack(trackId, retryCount)
      const verifiedUrl = await this.verifyAudioUrl(url)
      this.audioErrorMessage = ''
      if (!url || !verifiedUrl) {
        if (this.loadedWaveSurfer) {
          this.loadedWaveSurfer.pause()
          this.loadedWaveSurfer.destroy()
          this.loadedWaveSurfer = null
        }
        this.isPlaying = false
        this.loadedProductDetails = productDetails || this.loadedProductDetails
        this.showFooterBar = true
        this.footerBarVisible = true
        this.audioErrorMessage = 'Audio file unavailable. Please try again later.'
        return null
      }
      this.loadedProductDetails = productDetails
      this.isPlaying = false
      this.shouldPlay = !this.triggeredByScroll
      this.audioErrorMessage = ''
      return url
    },
    async checkTrackAvailability(trackId) {
      try {
        const url = await this.fetchTrack(trackId, 0)
        if (!url) return false;
        const verifiedUrl = await this.verifyAudioUrl(url)
        return !!verifiedUrl
      } catch (e) {
        return false
      }
    },
    async verifyAudioUrl(url) {
      try {
        const resp = await fetch(url, { method: 'HEAD', mode: 'cors' })
        if (resp.type === 'opaque') return true
        if (resp.ok) return true
        try {
          const respGet = await fetch(url, { method: 'GET', headers: { 'Range': 'bytes=0-0' }, mode: 'cors' })
          if (respGet.type === 'opaque') return true
          return respGet.ok
        } catch (eHeadGet) {
          return false
        }
      } catch (e) {
        try {
          const resp = await fetch(url, { method: 'GET', headers: { 'Range': 'bytes=0-0' }, mode: 'cors' })
          if (resp.type === 'opaque') return true
          return resp.ok
        } catch (e2) {
          return false
        }
      }
    },
    togglePlay(setPlaying = false) {
      if (!this.loadedWaveSurfer) {
        this.shouldPlay = true
        this.footerBarVisible = true
        this.showFooterBar = true
        return
      }
      if (setPlaying === true) {
        this.loadedWaveSurfer.play();
        this.isPlaying = true;
        this.shouldPlay = true;
        return
      }
      if (this.isPlaying) {
        this.loadedWaveSurfer.pause()
        this.isPlaying = false
        return
      } else {
        this.loadedWaveSurfer.play()
        this.isPlaying = true
        this.shouldPlay = true
        return
      }
    },
    async loadFooterAudio(productDetails, shouldPlay = false, triggeredByScroll = false) {
      this.loadedProductDetails = productDetails;
      this.showFooterBar = true;
      this.footerBarVisible = true;
      this.shouldPlay = shouldPlay && !triggeredByScroll;
      this.isPlaying = false;
      this.audioErrorMessage = '';
      this.triggeredByScroll = triggeredByScroll;
    },
    destroyLoadedWaveSurfer() {
      this.loadedWaveSurfer?.destroy()
    },
    makeFooterBarVisible() {
      this.footerBarVisible = true;
    },
    hideFooterBar() {
      this.footerBarVisible = false;
    },
    pauseOnly() {
      try {
        if (this.loadedWaveSurfer) {
          this.loadedWaveSurfer.pause()
        }
      } catch (e) {}
      this.isPlaying = false
    },
    stopPlayback(hideFooter = false, broadcast = true) {
      try {
        if (this.loadedWaveSurfer) {
          this.loadedWaveSurfer.pause()
          this.loadedWaveSurfer.destroy()
          this.loadedWaveSurfer = null
        }
      } catch (e) {}
      this.isPlaying = false
      this.shouldPlay = false
      this.audioErrorMessage = ''
      this.userClickedPlay = false
      if (hideFooter) {
        this.footerBarVisible = false
        this.showFooterBar = false
      }
    },
    setUserClickedPlay(value) {
      this.userClickedPlay = value
    },
  },
});